/**
 * GuardianEditScreen — a guardian edits their own profile or a child's.
 *
 * Two modes:
 *   'self'  → name and phone only. The email is the SSO username and the
 *             Emirates ID is the future UAE Pass join key, so neither is
 *             editable here.
 *   'child' → the fields the backend allows a guardian to change, and only
 *             while the application is still pending. Once the profile is
 *             approved, corrections go through the department.
 */
import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';

import { FONTS } from '../../constants/config';
import { PageContainer, Navbar, Breadcrumb, Toast } from '../../components';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useResponsive } from '../../hooks/useResponsive';
import { MainStackParamList } from '../../navigation/MainNavigator';
import api from '../../services/api';

type NavProp = NativeStackNavigationProp<MainStackParamList>;
type ScreenRoute = RouteProp<MainStackParamList, 'GuardianEdit'>;

const FOREGROUND = '#414042';
const BORDER = '#E2E8F0';
const MUTED = '#64748B';
const BRAND_RED = '#A9211F';

const EDITABLE_CHILD_STATUSES = ['applied', 'proposed'];

export default function GuardianEditScreen() {
  const navigation = useNavigation<NavProp>();
  const route = useRoute<ScreenRoute>();
  const mode = route.params?.mode ?? 'self';
  const studentId = route.params?.studentId;

  const { language } = useLanguage();
  const { colors } = useTheme();
  const { isMobile } = useResponsive();
  const isRTL = language === 'ar';

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toastMsg, setToastMsg] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  const [locked, setLocked] = useState(false);

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [school, setSchool] = useState('');
  const [gender, setGender] = useState('');
  const [nationality, setNationality] = useState('');
  const [schoolOptions, setSchoolOptions] = useState<{ value: string; label: string }[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (mode === 'self') {
          const resp = await api.get('/talent-students/guardian-me/');
          if (cancelled) return;
          setName(resp.data?.name || '');
          setPhone(resp.data?.phone || '');
        } else if (studentId) {
          const [child, schools] = await Promise.all([
            api.get(`/talent-students/${studentId}/`),
            api.get('/talent-register/schools/').catch(() => ({ data: [] })),
          ]);
          if (cancelled) return;
          const d = child.data || {};
          setName(d.name || '');
          setPhone(d.phone || '');
          setEmail(d.email || '');
          setSchool(d.school || '');
          setGender(d.gender || '');
          setNationality(d.nationality || '');
          setSchoolOptions(schools.data || []);
          // Mirrors the backend rule so the form does not offer an edit the
          // server will reject.
          setLocked(!EDITABLE_CHILD_STATUSES.includes(d.status));
        }
      } catch (e) {
        console.log('[GuardianEdit] load failed:', e);
        if (!cancelled) {
          setToastType('error');
          setToastMsg(isRTL ? 'تعذر تحميل البيانات' : 'Could not load the data');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [mode, studentId, isRTL]);

  const handleSave = async () => {
    if (!name.trim()) {
      setToastType('error');
      setToastMsg(isRTL ? 'الاسم مطلوب' : 'Name is required');
      return;
    }
    setSaving(true);
    try {
      if (mode === 'self') {
        await api.patch('/talent-students/guardian-me/', {
          name: name.trim(),
          phone: phone.trim(),
        });
      } else {
        await api.patch(`/talent-students/${studentId}/`, {
          name: name.trim(),
          phone: phone.trim(),
          email: email.trim(),
          school,
          gender,
          nationality: nationality.trim(),
        });
      }
      setToastType('success');
      setToastMsg(isRTL ? 'تم حفظ البيانات بنجاح' : 'Saved successfully');
      setTimeout(() => navigation.navigate('Guardian'), 1200);
    } catch (e: any) {
      setToastType('error');
      setToastMsg(
        e?.response?.data?.detail
        || e?.response?.data?.error
        || (isRTL ? 'تعذر حفظ البيانات' : 'Could not save'),
      );
    } finally {
      setSaving(false);
    }
  };

  const title = mode === 'self'
    ? (isRTL ? 'تعديل بياناتي' : 'Edit my details')
    : (isRTL ? 'تعديل بيانات الابن' : "Edit child's details");

  if (loading) {
    return (
      <PageContainer>
        <Navbar />
        <ActivityIndicator size="large" color={BRAND_RED} style={{ marginTop: 40 }} />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Navbar />
      <ScrollView contentContainerStyle={{ padding: isMobile ? 16 : 24 }}>
        <Breadcrumb
          items={[
            { label: isRTL ? 'أبنائي' : 'My children', onPress: () => navigation.navigate('Guardian') },
            { label: title },
          ]}
        />

        <View style={[styles.card, { backgroundColor: colors.card }]}>
          <Text style={[styles.title, { color: colors.textPrimary }]}>{title}</Text>

          {locked && (
            <Text style={styles.lockedNote}>
              {isRTL
                ? 'تم اعتماد تسجيل هذا الابن، لذلك لا يمكن تعديل بياناته من هنا. يرجى مراجعة الجهة لأي تصحيح.'
                : 'This child’s registration is approved, so details can no longer be edited here. Contact the department for corrections.'}
            </Text>
          )}

          <Text style={styles.label}>{isRTL ? 'الاسم' : 'Name'}</Text>
          <TextInput
            style={[styles.input, locked && styles.inputDisabled]}
            value={name}
            onChangeText={setName}
            editable={!locked}
            placeholderTextColor={MUTED}
          />

          <Text style={styles.label}>{isRTL ? 'رقم الهاتف' : 'Phone'}</Text>
          <TextInput
            style={[styles.input, locked && styles.inputDisabled]}
            value={phone}
            onChangeText={(v) => setPhone(v.replace(/[^0-9+]/g, ''))}
            editable={!locked}
            keyboardType="phone-pad"
            placeholderTextColor={MUTED}
          />

          {mode === 'child' && (
            <>
              <Text style={styles.label}>{isRTL ? 'البريد الإلكتروني' : 'Email'}</Text>
              <TextInput
                style={[styles.input, locked && styles.inputDisabled]}
                value={email}
                onChangeText={setEmail}
                editable={!locked}
                keyboardType="email-address"
                autoCapitalize="none"
                placeholderTextColor={MUTED}
              />

              <Text style={styles.label}>{isRTL ? 'المدرسة' : 'School'}</Text>
              {Platform.OS === 'web' ? (
                <select
                  value={school}
                  disabled={locked}
                  onChange={(e) => setSchool(e.target.value)}
                  style={{
                    backgroundColor: locked ? '#F1F5F9' : '#FFFFFF',
                    border: `1px solid ${BORDER}`,
                    borderRadius: 8,
                    padding: '10px 12px',
                    fontFamily: FONTS.regular,
                    fontSize: 14,
                    color: FOREGROUND,
                    textAlign: 'right',
                    width: '100%',
                    boxSizing: 'border-box' as any,
                    marginBottom: 4,
                  }}
                >
                  <option value="">{isRTL ? 'اختر المدرسة' : 'Select a school'}</option>
                  {schoolOptions.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              ) : (
                <TextInput
                  style={[styles.input, locked && styles.inputDisabled]}
                  value={school}
                  onChangeText={setSchool}
                  editable={!locked}
                  placeholderTextColor={MUTED}
                />
              )}

              <Text style={styles.label}>{isRTL ? 'الجنسية' : 'Nationality'}</Text>
              <TextInput
                style={[styles.input, locked && styles.inputDisabled]}
                value={nationality}
                onChangeText={setNationality}
                editable={!locked}
                placeholderTextColor={MUTED}
              />
            </>
          )}

          <View style={styles.actions}>
            <TouchableOpacity
              style={[styles.saveButton, (locked || saving) && styles.buttonDisabled]}
              onPress={handleSave}
              disabled={locked || saving}
              activeOpacity={0.85}
            >
              <Text style={styles.saveButtonText}>
                {saving ? (isRTL ? 'جارٍ الحفظ…' : 'Saving…') : (isRTL ? 'حفظ' : 'Save')}
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.cancelButton}
              onPress={() => navigation.navigate('Guardian')}
              activeOpacity={0.85}
            >
              <Text style={styles.cancelButtonText}>{isRTL ? 'إلغاء' : 'Cancel'}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </ScrollView>

      <Toast
        visible={toastMsg !== ''}
        message={toastMsg}
        type={toastType}
        onHide={() => setToastMsg('')}
      />
    </PageContainer>
  );
}

const styles = StyleSheet.create({
  card: {
    maxWidth: 720,
    width: '100%',
    alignSelf: 'center',
    borderWidth: 1,
    borderColor: BORDER,
    borderRadius: 12,
    padding: 24,
  },
  title: {
    fontFamily: FONTS.medium,
    fontSize: 20,
    textAlign: 'right',
    marginBottom: 20,
  },
  lockedNote: {
    fontFamily: FONTS.regular,
    fontSize: 13,
    color: '#92400E',
    backgroundColor: '#FEF9C3',
    borderRadius: 8,
    padding: 12,
    textAlign: 'right',
    marginBottom: 16,
  },
  label: {
    fontFamily: FONTS.regular,
    fontSize: 13,
    color: MUTED,
    textAlign: 'right',
    marginBottom: 6,
    marginTop: 12,
  },
  input: {
    borderWidth: 1,
    borderColor: BORDER,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontFamily: FONTS.regular,
    fontSize: 14,
    color: FOREGROUND,
    backgroundColor: '#FFFFFF',
    textAlign: 'right',
  },
  inputDisabled: {
    backgroundColor: '#F1F5F9',
    color: MUTED,
  },
  actions: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 24,
  },
  saveButton: {
    flex: 1,
    backgroundColor: BRAND_RED,
    borderRadius: 8,
    paddingVertical: 12,
    alignItems: 'center',
  },
  saveButtonText: {
    fontFamily: FONTS.medium,
    fontSize: 14,
    color: '#FFFFFF',
  },
  cancelButton: {
    flex: 1,
    borderWidth: 1,
    borderColor: BORDER,
    borderRadius: 8,
    paddingVertical: 12,
    alignItems: 'center',
  },
  cancelButtonText: {
    fontFamily: FONTS.medium,
    fontSize: 14,
    color: MUTED,
  },
  buttonDisabled: {
    opacity: 0.5,
  },
});
